/* eslint-disable modernloop/restrict-imports.cjs */
import React from 'react';

import { logError } from '@modernloop/shared/utils';

import KomboCard, { Props as KomboProps } from 'src/views-new/Settings/Integrations/Cards/KomboCard';

import { ListIntegrationResponse } from '../api/integration/useIntegrations';

import AtsSevice, { ExtensionSidebarParsedUrlType, ParsedAtsUrlResult, ScorecardGroupByStrategy } from './AtsService';

const getCandidateIdFromUrl = (url: URL): string | null => {
  const folders = url.pathname.split('/') || [];
  const contactsKeywordIndex = folders.indexOf('contacts');
  if (contactsKeywordIndex < 0) {
    return null;
  }

  return folders[contactsKeywordIndex + 1] || null;
};

const getApplicationIdFromUrl = (url: URL): string | null => {
  const folders = url.pathname.split('/') || [];
  const jobsKeywordIndex = folders.indexOf('jobs');
  if (jobsKeywordIndex < 0) {
    return null;
  }

  return url.searchParams.get('appId') || null;
};

class KulaAtsService implements AtsSevice {
  readonly featureFlag = 'show_kula_ats_integration';

  readonly name = 'Kula';

  readonly hostname = 'kula';

  readonly logoUrl = `/static/images/integrations/kula.svg`;

  readonly helpCenterUrl = '';

  readonly scorecardLabelPluralTranslationKey = 'hiring-dashboard-common:common.ats.scorecardsFallback';

  readonly noScorecardLabelTranslationKey = 'hiring-dashboard-common:common.ats.noScorecardFallback';

  readonly scorecardSelectTitleTranslationKey = 'hiring-dashboard-nav:nav.scorecards.selectTitle';

  readonly scorecardSearchPlaceholderTranslationKey = 'hiring-dashboard-nav:nav.scorecards.searchPlaceholder';

  readonly scorecardSearchPlaceholderEllipsisTranslationKey =
    'hiring-dashboard-nav:nav.scorecards.searchPlaceholderEllipsis';

  readonly isSourcingLinkEnabled = true;

  readonly isAtsUserSelectEnabled = false;

  readonly isAtsImportAvailabilityEnabled = false;

  readonly skipInterviewKitLinkTokenValidation = true;

  readonly scorecardGroupByKey = null;

  readonly scorecardGroupByStrategy = ScorecardGroupByStrategy.AtsJobStage;

  readonly scorecardSortKey = null;

  readonly showEstimatedMinutes = false;

  readonly createCandidatePermissionDialog = null;

  readonly showRequisitionId = false;

  readonly canSupportBrightHire = true;

  readonly hasScorecardSync = false;

  readonly scorecardSyncSettingLabelTranslationKey =
    'hiring-dashboard-common:common.ats.keepInterviewsInSyncWithAtsInterviewPlan';

  readonly hasHiringProcess = false;

  readonly hasScorecards = false;

  readonly hasEnforcedJobPermissions = false;

  readonly resetInterviewPlanWarningMessage =
    'The interview plan for this stage will be reset to its current settings in Kula.';

  // eslint-disable-next-line class-methods-use-this
  getIntegrationCard = (props: KomboProps, integrations?: ListIntegrationResponse): JSX.Element => {
    return <KomboCard {...props} kombo={integrations?.kombo} />;
  };

  parseExtensionSidebarUrl = (externalUrl: string): ParsedAtsUrlResult | null => {
    try {
      const url: URL | undefined = new URL(externalUrl);

      if (!url.hostname.includes(this.hostname)) {
        return null;
      }

      const candidateId = getCandidateIdFromUrl(url);
      if (candidateId) {
        return {
          type: ExtensionSidebarParsedUrlType.Candidate,
          atsCandidateId: candidateId,
          atsApplicationId: null,
        };
      }

      const applicationId = getApplicationIdFromUrl(url);
      if (applicationId) {
        return {
          type: ExtensionSidebarParsedUrlType.Candidate,
          atsCandidateId: null,
          atsApplicationId: applicationId,
        };
      }
    } catch (error) {
      logError('Error parsing Kula ATS url', error);
    }

    return null;
  };
}

export default new KulaAtsService();
